import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// actions
import appAction from '../../../redux/actions/app';
// types
import { stateType } from '../../../types/types';
// localization
import strings from '../../../localization';
// style
import './index.scss';

// Centered modal for global app flash messages (errors, success).
const FlashMessage: FC = () => {
  const dispatch = useDispatch();
  const flashMessage = useSelector((state: stateType) => state.app.flashMessage);

  // Clears the flash message from Redux.
  const handleClose = useCallback(() => {
    dispatch(appAction.clearFlashMessage());
  }, [dispatch]);

  // Closes the modal on Escape.
  useEffect(() => {
    if (!flashMessage) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [flashMessage, handleClose]);

  if (!flashMessage) {
    return null;
  }

  const titleId = 'flash-message-modal-title';

  return (
    <div
      className={`flash-message-modal flash-message-modal_${flashMessage.variant}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        className="flash-message-modal__backdrop"
        aria-label={strings.flashMessage.dismissAria}
        onClick={handleClose}
      />
      <div className="flash-message-modal__panel">
        <div className="flash-message-modal__header">
          <div className="flash-message-modal__title-row">
            <svg
              className="flash-message-modal__title-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <h2 id={titleId} className="flash-message-modal__title">
              {strings.flashMessage.title}
            </h2>
          </div>
          <button
            type="button"
            className="flash-message-modal__dismiss"
            aria-label={strings.flashMessage.dismissAria}
            onClick={handleClose}
          >
            <svg
              className="flash-message-modal__dismiss-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flash-message-modal__body">
          <p className="flash-message-modal__text">{flashMessage.message}</p>
        </div>
        <div className="flash-message-modal__actions">
          <button type="button" className="btn btn_submit flash-message-modal__close-btn" onClick={handleClose}>
            {strings.flashMessage.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlashMessage;
